/**
 * Organization helpers — public path + public API (A-005 / A-005.5).
 */

import api from "../../Environment";
import { organizationPublicPath as canonicalOrgPath } from "./entityRoutes";

export function organizationPublicPath(slug) {
  return canonicalOrgPath(slug);
}

/**
 * Fetch public organization payload by slug.
 * @param {string} slug
 * @returns {Promise<object|null>}
 */
export async function fetchOrganizationBySlug(slug) {
  const cleaned = String(slug || "").trim();
  if (!cleaned) {
    return null;
  }

  try {
    const response = await api.postMethod("organization/show", {
      slug: cleaned,
    });
    const body = response && response.data ? response.data : response;
    if (body && body.success && body.data) {
      return body.data;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Public organization directory (Community discovery).
 * @param {{ featured?: boolean, q?: string, page?: number, per_page?: number, organization_type?: string, type?: string, grouped?: boolean, per_type?: number }} [opts]
 * @returns {Promise<{ organizations: array, groups: array, types: array, pagination: object|null, total: number|null, error: boolean }>}
 */
export async function fetchOrganizationDirectory(opts = {}) {
  const input = {};
  if (opts.featured === true) {
    input.featured = 1;
  } else if (opts.featured === false) {
    input.featured = 0;
  }
  if (opts.q) {
    input.q = String(opts.q).trim();
  }
  if (opts.page) {
    input.page = opts.page;
  }
  if (opts.per_page) {
    input.per_page = opts.per_page;
  }
  if (opts.per_type) {
    input.per_type = opts.per_type;
  }
  if (opts.grouped) {
    input.grouped = 1;
  }
  const typeSlug = opts.organization_type || opts.type;
  if (typeSlug) {
    input.organization_type = String(typeSlug).trim();
  }

  try {
    const response = await api.postMethod("organization/list", input);
    const body = response && response.data ? response.data : response;
    if (body && body.success && body.data) {
      const organizations = Array.isArray(body.data.organizations)
        ? body.data.organizations
        : [];
      const groups = Array.isArray(body.data.groups) ? body.data.groups : [];
      const types = Array.isArray(body.data.types) ? body.data.types : [];
      return {
        organizations,
        groups,
        types,
        total: typeof body.data.total === "number" ? body.data.total : null,
        pagination: body.data.pagination || null,
        error: false,
      };
    }
    return {
      organizations: [],
      groups: [],
      types: [],
      total: null,
      pagination: null,
      error: true,
    };
  } catch (e) {
    return {
      organizations: [],
      groups: [],
      types: [],
      total: null,
      pagination: null,
      error: true,
    };
  }
}

/**
 * Organizations grouped by Organization Type (admin display_order).
 * @param {{ per_type?: number, q?: string, featured?: boolean }} [opts]
 */
export async function fetchOrganizationDirectoryGrouped(opts = {}) {
  return fetchOrganizationDirectory({
    ...opts,
    grouped: true,
    per_type: opts.per_type || 12,
  });
}
